import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import content from "@/content";

const EASE = [0.22, 1, 0.36, 1];

export default function Services() {
  const s = content.services;
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section id="services" data-testid="services-section" className="relative py-16 lg:py-24 bg-[#5E1620] text-linen texture-grain">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="text-[11px] uppercase tracking-[0.3em] text-goldsoft mb-6"
          >
            {s.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
            className="font-serif font-light text-4xl sm:text-5xl leading-tight"
            data-testid="services-heading"
          >
            {s.heading}
          </motion.h2>
        </div>

        <div className="border-t border-linen/15">
          {s.items.map((item, i) => {
            const open = openIndex === i;
            return (
              <motion.div
                key={item.index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.8, ease: EASE, delay: i * 0.06 }}
                className="border-b border-linen/15"
                data-testid={`service-item-${i}`}
              >
                  <button
                    data-testid={`service-toggle-${i}`}
                    onClick={() => setOpenIndex(open ? -1 : i)}
                    className="w-full py-7 lg:py-9 flex items-center gap-6 lg:gap-10 text-left group"
                  >
                    <span className="font-serif text-lg text-goldsoft/80 w-10 shrink-0">{item.index}</span>
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-serif font-light text-2xl sm:text-3xl lg:text-4xl transition-all duration-500 group-hover:translate-x-3 ${open ? "text-goldsoft translate-x-3" : "text-linen group-hover:text-goldsoft"}`}>
                        {item.title}
                      </h3>
                      <p className="mt-1 text-xs sm:text-sm text-linen/50 italic font-serif">{item.short}</p>
                    </div>
                    <motion.span
                      animate={{ rotate: open ? 45 : 0 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      className={`shrink-0 w-10 h-10 border flex items-center justify-center transition-colors duration-500 ${open ? "border-goldsoft text-goldsoft" : "border-linen/30 text-linen/70 group-hover:border-goldsoft group-hover:text-goldsoft"}`}
                    >
                      <Plus size={16} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.6, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <p className="pb-9 pl-14 sm:pl-16 lg:pl-20 pr-2 sm:pr-4 max-w-2xl text-sm sm:text-base text-linen/70 leading-relaxed" data-testid={`service-desc-${i}`}>
                          {item.description}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
        </div>
      </div>
    </section>
  );
}
